import React from 'react'
import PropTypes from 'prop-types'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import { CATEGORIES, subCategoryLabel } from '../helpers/templateCategories'
import { ICON, RADIUS, STATUS, TEXT } from '../theme/tokens'

const categoryLabel = (value) =>
    CATEGORIES.find((c) => c.value === value)?.label ?? value

/**
 * What the service says when it refuses a save because the category and sub-category do not fit the template
 * (422, `error_object.kind` "tag_mismatch"): what is wrong, why, the recommended value for each, and a button
 * that applies them. The person changes either or both (or edits the template) and presses the same submit
 * button again, which checks again.
 *
 * `detail` is the service's `error_object`: `scope` ("meta" or "internal"), `reason`, and for `category` and
 * `sub_category` an object with `fits` and `recommended`.
 */
const TagMismatchPanel = ({ detail, onUse, submitLabel }) => {
    const wrong = []
    const suggested = []
    if (!detail.category.fits) {
        wrong.push('category')
        suggested.push(
            `Category: ${categoryLabel(detail.category.recommended)}`
        )
    }
    if (!detail.sub_category.fits) {
        wrong.push('sub-category')
        suggested.push(
            `Sub-category: ${subCategoryLabel(detail.sub_category.recommended)}`
        )
    }
    const rules =
        detail.scope === 'meta'
            ? "Meta's categorization rules"
            : 'our category rules'
    return (
        <Box
            role="alert"
            data-testid="tag-mismatch"
            sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                gap: 1.5,
                alignItems: { xs: 'stretch', sm: 'flex-start' },
                background: STATUS.warning.bg,
                border: `1px solid ${STATUS.warning.border}`,
                borderRadius: `${RADIUS.sm}px`,
                px: 1.5,
                py: 1.25,
            }}
        >
            <WarningAmberIcon
                sx={{ fontSize: ICON.lg, color: STATUS.warning.fg, mt: '2px' }}
            />
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                    variant="body2"
                    sx={{ fontWeight: 600, color: TEXT.primary }}
                >
                    {`The ${wrong.join(' and ')} ${
                        wrong.length > 1 ? 'do' : 'does'
                    } not match this template under ${rules}.`}
                </Typography>
                {detail.reason && (
                    <Typography
                        variant="caption"
                        component="p"
                        sx={{ color: TEXT.muted, mt: 0.5 }}
                    >
                        {detail.reason}
                    </Typography>
                )}
                <Typography
                    variant="caption"
                    component="p"
                    sx={{ color: TEXT.primary, mt: 0.75 }}
                >
                    {`Suggested: ${suggested.join(' · ')}`}
                </Typography>
                <Typography
                    variant="caption"
                    component="p"
                    sx={{ color: TEXT.muted, mt: 0.5 }}
                >
                    {`Use the suggested category or sub-category, or edit the template if needed, then press ${submitLabel} again. It is checked again before it is saved.`}
                </Typography>
            </Box>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: { xs: 'row', sm: 'column' },
                    flexWrap: 'wrap',
                    gap: 0.75,
                }}
            >
                <Button variant="outlined" size="small" onClick={onUse}>
                    Use suggested category
                </Button>
            </Box>
        </Box>
    )
}

TagMismatchPanel.propTypes = {
    detail: PropTypes.shape({
        scope: PropTypes.string,
        reason: PropTypes.string,
        category: PropTypes.shape({
            fits: PropTypes.bool,
            recommended: PropTypes.string,
        }).isRequired,
        sub_category: PropTypes.shape({
            fits: PropTypes.bool,
            recommended: PropTypes.string,
        }).isRequired,
    }).isRequired,
    onUse: PropTypes.func.isRequired,
    submitLabel: PropTypes.string.isRequired,
}

export default TagMismatchPanel
